import { useMutation } from '@tanstack/react-query'
import { parseProfileValue } from '@/services/users/userProfileApi'
import { useUserProfile } from '../contexts/UserProfileContext'
import { useNetwork } from '../contexts/NetworkContext'
import type { DateRange } from '@/services/stats/statsRange'
import type {
  Breakdown,
  PoliticalFilter,
  QueryObjectType,
  QuerySide,
  SubBreakdown,
} from '@/services/stats/statsQuery'

/** Profile key the saved quick reports live under — this UI's own. */
export const SAVED_QUERIES_KEY = 'ruiSavedQueries'

/** Oldest ones fall off past this, so the profile value stays a sensible size. */
const MAX_SAVED = 50

/**
 * A quick report as picked, not as run: the period is kept as the choice made
 * ("last 30 days"), so loading it next month reports on next month's 30 days.
 */
export interface SavedQuery {
  id: string
  name: string
  side: QuerySide
  /** Object ids only mean something inside the network they were picked in. */
  networkId: string
  objectType: QueryObjectType
  breakdown: Breakdown
  subBreakdown: SubBreakdown | null
  /** Names kept alongside ids so the picker can show them without a lookup. */
  objects: { id: string; name?: string }[]
  /** Parents picked to bring in their objects too; missing on older saves. */
  parents?: Partial<Record<QueryObjectType, { id: string; name?: string }[]>>
  range: DateRange
  /** Older saves may hold '', read as the network's first currency. */
  currency: string
  political: PoliticalFilter
  includeAll: boolean
  savedAt: string
}

export type SavedQueryInput = Omit<SavedQuery, 'id' | 'side' | 'networkId' | 'savedAt'>

/**
 * The user's saved quick reports for one module in the current network, kept
 * in their user profile so they follow them between browsers.
 */
export function useSavedQueries(side: QuerySide) {
  const { currentNetwork } = useNetwork()
  const networkId = currentNetwork?.id ?? ''
  const { read, latest, save: saveProfile, isLoading } = useUserProfile()
  const stored = read<unknown>(SAVED_QUERIES_KEY, [])
  const all = Array.isArray(stored) ? (stored as SavedQuery[]) : []

  // Every write starts from what the server has now, so a query saved in
  // another tab is not lost to this tab's older copy.
  const write = useMutation({
    mutationFn: async (change: (current: SavedQuery[]) => SavedQuery[]) => {
      const current = parseProfileValue<unknown>((await latest())[SAVED_QUERIES_KEY], [])
      const next = change(Array.isArray(current) ? (current as SavedQuery[]) : []).slice(0, MAX_SAVED)
      await saveProfile({ [SAVED_QUERIES_KEY]: next })
      return next
    },
  })

  const saved = all
    .filter((q) => q.side === side && q.networkId === networkId)
    .sort((a, b) => a.name.localeCompare(b.name))

  /** Saves a new query, or overwrites the one with the same name in this module. */
  const save = (input: SavedQueryInput) =>
    write.mutateAsync((current) => {
      const existing = current.find(
        (q) => q.side === side && q.networkId === networkId && q.name.trim().toLowerCase() === input.name.trim().toLowerCase()
      )
      const entry: SavedQuery = {
        ...input,
        name: input.name.trim(),
        id: existing?.id ?? crypto.randomUUID(),
        side,
        networkId,
        savedAt: new Date().toISOString(),
      }
      return [entry, ...current.filter((q) => q.id !== entry.id)]
    })

  const remove = (id: string) => write.mutateAsync((current) => current.filter((q) => q.id !== id))

  return { saved, isLoading, save, remove, isSaving: write.isPending }
}
